import { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { useWindowSize } from '@/hooks/useWindowSize';
import imgStep1 from './step_1.png';
import imgStep2 from './step_2.png';
import imgStep3 from './step_3.png';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const id = MODAL_IDS.decision_widget_onboarding;
const TOTAL_STEPS = 3;
export const ONBOARDING_STORAGE_KEY = 'decision_widget_onboarding_shown';

const Step = ({
  image,
  title,
  description,
  stepIdx,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
}) => (
  <div className={s.modal}>
    <div className={s.illustration_wrap}>
      <img src={image} alt="" width={472} height={240} className={s.illustration} />
    </div>
    <div className={s.body}>
      <h2 className={s.title}>{title}</h2>
      <p className={s.description}>{description}</p>
    </div>
    <div className={s.dots_section}>
      <div className={s.dots}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <span key={i} className={s.dot_item}>
            <span className={classnames(s.dot, i === stepIdx && s.dot_active)} />
          </span>
        ))}
      </div>
    </div>
    <div className={s.actions}>
      {secondaryLabel && (
        <button
          type="button"
          onClick={onSecondary}
          className={classnames(r.grey_base, s.button_full)}
        >
          {secondaryLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onPrimary}
        className={classnames(r.black_base, s.button_full)}
      >
        {primaryLabel}
      </button>
    </div>
  </div>
);

export const DecisionWidgetOnboardingModal = () => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal />;
};

const Modal = () => {
  const [stepIdx, setStepIdx] = useState(0);
  const { closeModal } = useModal();
  const { isMobile } = useWindowSize();
  const { t } = useTranslation();

  const onClose = () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, '1');
    closeModal(id);
  };

  const renderStep = () => {
    if (stepIdx === 0) {
      return (
        <Step
          image={imgStep1}
          title={t('modal:decision_widget_onboarding_modal.step1_title')}
          description={t('modal:decision_widget_onboarding_modal.step1_description')}
          primaryLabel={t('modal:decision_widget_onboarding_modal.btn_next')}
          onPrimary={() => setStepIdx(1)}
          stepIdx={0}
        />
      );
    }
    if (stepIdx === 1) {
      return (
        <Step
          image={imgStep2}
          title={t('modal:decision_widget_onboarding_modal.step2_title')}
          description={t('modal:decision_widget_onboarding_modal.step2_description')}
          primaryLabel={t('modal:decision_widget_onboarding_modal.btn_next')}
          secondaryLabel={t('modal:decision_widget_onboarding_modal.btn_back')}
          onPrimary={() => setStepIdx(2)}
          onSecondary={() => setStepIdx(0)}
          stepIdx={1}
        />
      );
    }
    return (
      <Step
        image={imgStep3}
        title={t('modal:decision_widget_onboarding_modal.step3_title')}
        description={t('modal:decision_widget_onboarding_modal.step3_description')}
        primaryLabel={t('modal:decision_widget_onboarding_modal.btn_got_it')}
        secondaryLabel={t('modal:decision_widget_onboarding_modal.btn_back')}
        onPrimary={onClose}
        onSecondary={() => setStepIdx(1)}
        stepIdx={2}
      />
    );
  };

  const content = (
    <div className={s.wrapper}>
      {renderStep()}
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalCenterLayout>
  );
};